import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
} from '@angular/core';
import { Item } from '../../../common/entity/entities';
import { FormBuilder } from '../../../common/builder/gs.form.builder';
import { FormTypes } from '../../../common/entity/enum/formTypes';
import { TranslateService } from '@ngx-translate/core';
import { FormDropdownProperty } from '../../../common/entity/form/formDropdown.property';
import { ReportingClient } from '../../../client/reporting.client';
import { FormButtonProperty } from '../../../common/entity/form/formButton.property';

@Component({
  selector: 'change-log-filter',
  templateUrl: './changeLogFilter.control.html',
  styles: [`
    :host ::ng-deep .rollback-btn.p-button {
      font-size: 0.85rem;
    }
  `],
})
export class ChangeLogFilterControl implements OnInit, OnDestroy {
  //#region -------Properties
  @Input() parent: Item;
  @Input() showSelectType: boolean = false;
  @Input() showRollbackPOSDataButton: boolean = false;
  @Input() showRollbackCalculatedDataButton: boolean = false;

  @Output() onLoad: EventEmitter<any> = new EventEmitter<any>();
  @Output() onRollbackPOSData: EventEmitter<any> = new EventEmitter<any>();
  @Output() onRollbackCalculatedData: EventEmitter<any> = new EventEmitter<any>();
  itemName = 'changeLogFilter';

  get item() {
    return this.parent.items.get(this.itemName);
  }

  get loadButton(): FormButtonProperty {
    if (this.properties) return this.properties.find((p) => p.name == 'Load');
  }

  get rollbackPOSDataButton(): FormButtonProperty {
    if (this.properties) return this.properties.find((p) => p.name == 'Rollback');
  }

  get rollbackCalculatedDataButton(): FormButtonProperty {
    if (this.properties) return this.properties.find((p) => p.name == 'RollbackCalculatedData');
  }

  properties;
  //#endregion

  //#region -------Constructor
  constructor(
    private translate: TranslateService,
    private reportingClient: ReportingClient
  ) {}
  //#endregion

  //#region -------NG Events
  ngOnInit() {
    this.initiate();
  }

  ngOnDestroy() {
    this.loadButton?.click.unsubscribe();
    this.rollbackPOSDataButton?.click.unsubscribe();
    this.rollbackCalculatedDataButton?.click.unsubscribe();
  }
  //#endregion

  //#region -------UI Events
  propertyChange(e) {
    this.loadButton.disabled = !this.item.isValid;
    this.rollbackPOSDataButton.disabled = true;
    this.rollbackCalculatedDataButton.disabled = true;
    if (e.propertyName == 'Location') {
      this.item.data.CustomerIDs = [];
      e.newValue.forEach((val) => {
        let data = e.property.options.find((o) => o.value == val).data;
        this.item.data.CustomerIDs.push(data.ID);
      });
    }
    this.updateCalculatedDataRollbackVisibility();
  }

  load = (e) => {
    this.onLoad.emit(this.item.data);
    this.rollbackPOSDataButton.disabled = !this.item.isValid;
    this.rollbackCalculatedDataButton.disabled = !this.item.isValid;
  };

  rollbackPOSData = (e) => {
    this.onRollbackPOSData.emit(this.item.data);
  };

  rollbackCalculatedData = (e) => {
    this.onRollbackCalculatedData.emit(this.item.data);
  };
  //#endregion

  //#region -------Private
  initiate() {
    if (!this.parent.items.get(this.itemName)) this.setItem();
    this.properties = FormBuilder.build(
      FormTypes.ChangeLogFilter,
      this.translate
    );
    if (this.showSelectType) this.setTypeOptions();
    this.setLocationOptions();
    this.loadButton.click.subscribe(this.load);
    this.loadButton.disabled = !this.item.isValid;

    this.rollbackPOSDataButton.click.subscribe(this.rollbackPOSData);
    this.rollbackPOSDataButton.disabled = true;
    this.rollbackPOSDataButton.visible = this.showRollbackPOSDataButton;
    this.rollbackPOSDataButton.styleClass = 'rollback-btn';

    this.rollbackCalculatedDataButton.click.subscribe(this.rollbackCalculatedData);
    this.rollbackCalculatedDataButton.disabled = true;
    this.rollbackCalculatedDataButton.visible = false;
    this.rollbackCalculatedDataButton.styleClass = 'rollback-btn';


  }

  setItem() {
    let data = {
      Type: 0,
      DateRange: undefined,
      Location: undefined,
      CustomerIDs: undefined,
    };
    this.parent.setItem(data, this.itemName);
  }

  clear() {
    this.item.data = {
      Type: 0,
      DateRange: undefined,
      Location: undefined,
      CustomerIDs: undefined,
    };
  }

  setTypeOptions() {
    let prop: FormDropdownProperty = this.properties.find(
      (p) => p.name == 'Type'
    );
    prop.visible = true;
    prop.options = [];
    prop.options.push({ label: 'All', value: 0 });
    prop.options.push({ label: 'POS data changes', value: 1 });
    prop.options.push({ label: 'Calculated data changes', value: 2 });
  }

  updateCalculatedDataRollbackVisibility() {
    if (!this.showRollbackCalculatedDataButton) {
      this.rollbackCalculatedDataButton.visible = false;
      return;
    }

    let locations = this.item.data.Location;
    let dateRange = this.item.data.DateRange;

    if (!locations || !locations.length || !dateRange || !dateRange[1]) {
      this.rollbackCalculatedDataButton.visible = false;
      return;
    }

    let dateTo = new Date(dateRange[1]);
    let locationProp: FormDropdownProperty = this.properties.find((p) => p.name == 'Location');

    let allHaveNewEngine = locations.every((locCode) => {
      let option = locationProp.options.find((o) => o.value == locCode);
      let newEngineActiveFrom = option?.data?.NewEngineActiveFrom;
      return newEngineActiveFrom != null && new Date(newEngineActiveFrom) <= dateTo;
    });

    this.rollbackCalculatedDataButton.visible = allHaveNewEngine;
  }

  setLocationOptions() {
    let prop: FormDropdownProperty = this.properties.find(
      (p) => p.name == 'Location'
    );
    this.reportingClient.getActiveCustomers().then((response) => {
      prop.options = [];
      if (
        response?.result.Customers &&
        response?.result.Customers instanceof Array
      ) {
        response?.result.Customers.forEach((val) => {
          prop.options.push(
            {
              label: val.Name,
              value: val.Code,
              disabled: (val?.Disabled == true || val?.CustomerBlocked == true) ?? false,
              data: val
            })
        });
      }

      this.loadButton.disabled = !this.item.isValid;
      this.rollbackPOSDataButton.disabled = true;
      this.rollbackCalculatedDataButton.disabled = true;
    });
  }
  //#endregion
}
